import React, { useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  Lightbulb,
  Zap,
  Shield,
  Brain,
  Database,
  Filter,
  ArrowRight,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { AIAgent } from "./types";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  BubbleController,
} from "chart.js";
import { Bubble } from "react-chartjs-2";
import { topics } from "./data/data";
import { D3Chart } from "./D3Chart";
import { calculateTotalTests, extractRecommendationsFromData } from "./utils";

// Define the plugin at the top level
const simpleBubbleLabels = {
  id: "simpleBubbleLabels",
  afterDatasetsDraw: (chart: any) => {
    const ctx = chart.ctx;
    const dataset = chart.data.datasets[0];
    const meta = chart.getDatasetMeta(0);
    dataset.data.forEach((point: any, i: number) => {
      const element = meta.data[i];
      if (!element) return;
      const { x, y } = element.getCenterPoint();
      ctx.save();
      ctx.font = "14px Arial";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#fff";
      ctx.fillText(point.label, x, y);
      ctx.restore();
    });
  },
};

// Register the plugin globally
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
  BubbleController,
  simpleBubbleLabels,
);

// Fake data for bubble chart - topics with test results
const bubbleChartData = {
  datasets: [
    {
      label: "Agent Topics",
      data: topics.map((topic: any) => ({
        x: topic.successRate, // Accuracy (%)
        y: topic.topicCoverage, // Topic Coverage (%)
        r: topic.totalTests * 4 + 50, // Bubble size (scaled for visibility)
        label: topic.topic,
        passed: topic.status === "passed",
      })),
      backgroundColor: [
        "rgba(34, 197, 94, 0.6)",
        "rgba(34, 197, 94, 0.6)",
        "rgba(239, 68, 68, 0.6)",
        "rgba(34, 197, 94, 0.6)",
        "rgba(239, 68, 68, 0.6)",
      ],
      borderColor: [
        "rgba(34, 197, 94, 1)",
        "rgba(34, 197, 94, 1)",
        "rgba(239, 68, 68, 1)",
        "rgba(34, 197, 94, 1)",
        "rgba(239, 68, 68, 1)",
      ],
      borderWidth: 2,
    },
  ],
};

const bubbleChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: "rgba(31, 41, 55, 0.95)",
      titleColor: "#f3f4f6",
      bodyColor: "#d1d5db",
      borderColor: "#4b5563",
      borderWidth: 1,
      callbacks: {
        label: function (context: any) {
          const data = context.raw;
          return [
            `Topic: ${data.label}`,
            `Accuracy: ${data.x}%`,
            `Topic Coverage: ${data.y}%`,
            `Test Count: ${data.r}`,
            `Status: ${data.passed ? "✅ Passed" : "❌ Failed"}`,
          ];
        },
      },
    },
  },
  scales: {
    x: {
      title: {
        display: true,
        text: "Accuracy (%)",
        color: "#9ca3af",
        font: { size: 12 },
      },
      grid: { color: "rgba(75, 85, 99, 0.3)" },
      ticks: { color: "#9ca3af" },
    },
    y: {
      title: {
        display: true,
        text: "Topic Coverage (%)",
        color: "#9ca3af",
        font: { size: 12 },
      },
      grid: { color: "rgba(75, 85, 99, 0.3)" },
      ticks: { color: "#9ca3af" },
    },
  },
};

// Action items for agent improvement with failed test examples
const improvementActions = [
  {
    id: "2",
    title: "Improve Personalization",
    description: "82% accuracy - inconsistent experience",
    priority: "medium" as const,
    topic: "Customer Personalization",
    actions: [
      "Add customer memory system",
      "Implement seasonal recommendations",
      "Improve emotional intelligence",
    ],
    icon: Brain,
    failedTests: [
      {
        id: "cp-001",
        testName: "Customer History",
        input: "Customer: I bought Advanced Génifique last month, what's new?",
        expectedOutput:
          "Welcome back! Try our new Light Pearl serum or limited edition Rose Extrait",
        actualOutput: "We have many new products. What are you looking for?",
        responseTime: 2200,
        severity: "medium" as const,
      },
      {
        id: "cp-002",
        testName: "Gift Recommendations",
        input: "Customer: I need a gift for my mother's birthday",
        expectedOutput:
          "I recommend our luxurious Absolue collection or La Vie Est Belle perfume",
        actualOutput: "We have many products. What's your budget?",
        responseTime: 1900,
        severity: "medium" as const,
      },
    ],
  },
  {
    id: "3",
    title: "Fix Product Knowledge",
    description: "75% accuracy - below luxury standards",
    priority: "high" as const,
    topic: "Skincare Knowledge",
    actions: [
      "Expand training data",
      "Add ingredient knowledge",
      "Improve product comparisons",
    ],
    icon: Database,
    failedTests: [
      {
        id: "pk-001",
        testName: "Ingredient Knowledge",
        input:
          "Customer: What's the difference between hyaluronic acid and peptides?",
        expectedOutput:
          "Hyaluronic acid provides hydration, peptides stimulate collagen production",
        actualOutput: "Both are good for skin. They're in many of our products",
        responseTime: 3500,
        severity: "high" as const,
      },
      {
        id: "pk-002",
        testName: "Product Comparison",
        input: "Customer: Should I choose Advanced Génifique or Absolue?",
        expectedOutput:
          "Advanced Génifique is best-selling anti-aging, Absolue is most luxurious with rare ingredients",
        actualOutput: "Both are good products. Which one do you prefer?",
        responseTime: 2800,
        severity: "high" as const,
      },
    ],
  },
  {
    id: "4",
    title: "Optimize Response Times",
    description: "Above 200ms threshold",
    priority: "medium" as const,
    topic: "General Performance",
    actions: [
      "Add response caching",
      "Optimize inference pipeline",
      "Scale resources",
    ],
    icon: Zap,
    failedTests: [
      {
        id: "rt-001",
        testName: "Response Time",
        input: "Complex product recommendation",
        expectedOutput: "Response time < 200ms",
        actualOutput: "Response time: 450ms (250ms over)",
        responseTime: 450,
        severity: "medium" as const,
      },
      {
        id: "rt-002",
        testName: "Concurrent Handling",
        input: "5 simultaneous queries",
        expectedOutput: "All responses within 300ms",
        actualOutput: "Average: 550ms, 2 customers delayed",
        responseTime: 550,
        severity: "medium" as const,
      },
    ],
  },
];

interface FailedTest {
  id: string;
  testName: string;
  input: string;
  expectedOutput: string;
  actualOutput: string;
  responseTime: number;
  severity: "high" | "medium" | "low";
}

interface ImprovementAction {
  id: string;
  title: string;
  description: string;
  priority: "critical" | "high" | "medium" | "low";
  topic: string;
  actions: string[];
  icon: any;
  failedTests: FailedTest[];
}

const BubbleChart: React.FC = () => {
  const chartRef = React.useRef<any>(null);
  const [labelPositions, setLabelPositions] = React.useState<any[]>([]);

  React.useEffect(() => {
    if (chartRef.current) {
      const chart = chartRef.current;
      const positions = bubbleChartData.datasets[0].data
        .map((point: any, index: number) => {
          const meta = chart.getDatasetMeta(0);
          const element = meta.data[index];

          if (element) {
            const { x, y } = element.getCenterPoint();
            return { x, y, label: point.label, size: point.r };
          }
          return null;
        })
        .filter(Boolean);

      setLabelPositions(positions);
    }
  }, []);

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardContent>
        <div className="h-80 relative">
          <Bubble
            ref={chartRef}
            data={bubbleChartData}
            options={bubbleChartOptions}
          />
        </div>
      </CardContent>
    </Card>
  );
};

const ImprovementActions: React.FC = () => {
  const [selectedTopic, setSelectedTopic] = useState<string>("all");
  const navigate = useNavigate();

  // Get actual recommendations from data
  const actualRecommendations = extractRecommendationsFromData();

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "critical":
        return "bg-red-900/50 text-red-300 border-red-500/50";
      case "high":
        return "bg-red-900/30 text-red-400 border-red-600/30";
      case "medium":
        return "bg-yellow-900/30 text-yellow-400 border-yellow-600/30";
      case "low":
        return "bg-blue-900/30 text-blue-400 border-blue-600/30";
      default:
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "high":
        return "bg-red-900/30 text-red-400 border-red-600/30";
      case "medium":
        return "bg-yellow-900/30 text-yellow-400 border-yellow-600/30";
      case "low":
        return "bg-blue-900/30 text-blue-400 border-blue-600/30";
      default:
        return "bg-gray-900/30 text-gray-400 border-gray-600/30";
    }
  };

  // Sort actions by priority (critical -> high -> medium -> low)
  const sortActionsByPriority = (actions: ImprovementAction[]) => {
    const priorityOrder = { critical: 4, high: 3, medium: 2, low: 1 };
    return actions.sort((a, b) => {
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
  };

  const filteredActions =
    selectedTopic === "all"
      ? sortActionsByPriority(actualRecommendations)
      : sortActionsByPriority(
          actualRecommendations.filter(
            (action) => action.topic === selectedTopic,
          ),
        );

  const handleActionClick = (action: ImprovementAction) => {
    navigate({
      to: "/ai-agents/$agentId/topics/$topicId",
      params: { agentId: "1", topicId: action.id },
    });
  };

  return (
    <>
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CardTitle className="text-gray-100 flex items-center space-x-2">
                <Lightbulb className="h-5 w-5 text-yellow-400" />
                <span>Recommended Actions</span>
              </CardTitle>
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-gray-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {filteredActions.length === 0 ? (
            <div className="text-center py-8">
              <Lightbulb className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">
                No recommendations available at this time.
              </p>
              <p className="text-sm text-gray-500 mt-2">
                All areas are performing well!
              </p>
            </div>
          ) : (
            filteredActions.map((action: any) => (
              <div
                key={action.id}
                className="border border-gray-700 rounded-lg p-4 bg-gray-750 hover:bg-gray-700 transition-colors cursor-pointer"
                onClick={() => handleActionClick(action)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start space-x-3">
                    <action.icon className="h-5 w-5 text-blue-400 mt-1" />
                    <div>
                      <h4 className="font-medium text-gray-100">
                        {action.title}
                      </h4>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge className="bg-gray-600 text-gray-300 border-gray-500">
                      {action.topic}
                    </Badge>
                    <Badge
                      className={`${getPriorityColor(action.priority)} border`}
                    >
                      {action.priority.toUpperCase()}
                    </Badge>
                  </div>
                </div>

                <ul>
                  {action.actions.map((item: string, index: number) => (
                    <li
                      key={index}
                      className="text-sm text-gray-400 flex items-start space-x-2"
                    >
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </>
  );
};

// D3 Chart Component

export const OverviewTab: React.FC<{ agent: AIAgent }> = ({ agent }) => {
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [isAtRoot, setIsAtRoot] = useState<boolean>(true);
  const navigate = useNavigate();
  const { agentId } = useParams({ from: "/ai-agents/$agentId" });

  const handleNodeSelect = (nodeData: any) => {
    setSelectedNode(nodeData);
  };

  const handleZoomChange = (isAtRootLevel: boolean) => {
    setIsAtRoot(isAtRootLevel);
  };

  const getNodeInfo = (node: any) => {
    if (!node) return null;

    if (node.isTest) {
      // Test node - show test-specific metrics
      return {
        title: node.originalInput || node.name,
        type: "test",
        data: {
          productionReadiness: node.testData.success ? 100 : 0,
          coverage: 100, // Individual test has 100% coverage
          repeatability: node.testData.success ? 100 : 0,
          aiGeneratedTests: 1,
          testsPassed: node.testData.success ? 1 : 0,
          testsFailed: node.testData.success ? 0 : 1,
          totalTests: 1,
        },
      };
    } else {
      // Topic node - show topic-specific metrics
      const totals = calculateTotalTests(node);
      const passRate =
        totals.total > 0 ? (totals.passed / totals.total) * 100 : 0;

      return {
        title: node.name,
        type: "topic",
        data: {
          productionReadiness: passRate, // Use pass rate as production readiness
          coverage: node.value || 0,
          repeatability: passRate, // Use pass rate as repeatability
          aiGeneratedTests: totals.total,
          testsPassed: totals.passed,
          testsFailed: totals.failed,
          totalTests: totals.total,
        },
      };
    }
  };

  const nodeInfo = getNodeInfo(selectedNode);

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="flex flex-col lg:flex-row lg:max-h-[500px]">
        <div className="flex-1">
          <D3Chart
            onNodeSelect={handleNodeSelect}
            onZoomChange={handleZoomChange}
          />
        </div>

        <Card className="mt-4 lg:mt-0 lg:ml-6 bg-gray-800 border-gray-700 flex flex-col justify-between border rounded-xl p-6 min-w-[320px] max-w-md lg:h-[500px]">
          <div className="flex flex-col h-full">
            <div className="flex-1">
              <div className="flex justify-center items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-gray-100">
                  {!isAtRoot && nodeInfo
                    ? nodeInfo.title
                    : "Beauty Sales Agent"}
                </h3>
                {!isAtRoot && nodeInfo && nodeInfo.type === "test" && (
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      nodeInfo.data.productionReadiness === 100
                        ? "bg-green-600 text-white"
                        : "bg-red-600 text-white"
                    }`}
                  >
                    {nodeInfo.data.productionReadiness === 100
                      ? "PASSED"
                      : "FAILED"}
                  </span>
                )}
              </div>
              <div className="space-y-3">
                {!isAtRoot && nodeInfo ? (
                  <>
                    {nodeInfo.type === "test" ? (
                      // Test-specific display
                      <div className="space-y-4 max-h-64 overflow-y-auto">
                        {/* Expected Output Card */}
                        <div className="p-4 rounded-lg border border-gray-600 bg-gray-800/50">
                          <span className="text-sm font-medium text-gray-300 mb-2 block">
                            Expected Output
                          </span>
                          <p className="text-sm text-gray-200 leading-relaxed">
                            {selectedNode.testData?.expected ||
                              "No expected output specified"}
                          </p>
                        </div>

                        {/* Received Output Card */}
                        <div
                          className={`p-4 rounded-lg border ${
                            nodeInfo.data.productionReadiness === 100
                              ? "border-green-600/30 bg-green-900/10"
                              : "border-red-600/30 bg-red-900/10"
                          }`}
                        >
                          <span className="text-sm font-medium text-gray-300 mb-2 block">
                            Received Output
                          </span>
                          <p className="text-sm text-gray-200 leading-relaxed">
                            {selectedNode.testData?.received ||
                              "No output received"}
                          </p>
                        </div>
                      </div>
                    ) : (
                      // Topic-specific display (original metrics)
                      <>
                        <div>
                          <span className="text-sm font-medium text-gray-300">
                            Production Readiness
                          </span>
                          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden mt-1">
                            <div
                              className="bg-blue-500 h-4 transition-all duration-700"
                              style={{
                                width: `${nodeInfo.data.productionReadiness}%`,
                                minWidth: "8%",
                                borderRadius: "9999px",
                              }}
                            />
                          </div>
                          <div className="text-xs text-gray-500 mt-1 text-right">
                            {Math.round(nodeInfo.data.productionReadiness)}%
                            ready for production
                          </div>
                        </div>

                        <div>
                          <span className="text-sm font-medium text-gray-300">
                            Coverage
                          </span>
                          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden mt-1">
                            <div
                              className="bg-blue-500 h-4 transition-all duration-700"
                              style={{
                                width: `${nodeInfo.data.coverage}%`,
                                minWidth: "8%",
                                borderRadius: "9999px",
                              }}
                            />
                          </div>
                          <div className="text-xs text-gray-500 mt-1 text-right">
                            {Math.round(nodeInfo.data.coverage)}% coverage
                          </div>
                        </div>

                        <div>
                          <span className="text-sm font-medium text-gray-300">
                            Repeatability
                          </span>
                          <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden mt-1">
                            <div
                              className="bg-blue-500 h-4 transition-all duration-700"
                              style={{
                                width: `${nodeInfo.data.repeatability}%`,
                                minWidth: "8%",
                                borderRadius: "9999px",
                              }}
                            />
                          </div>
                          <div className="text-xs text-gray-500 mt-1 text-right">
                            {Math.round(nodeInfo.data.repeatability)}%
                            repeatability
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 text-sm">
                          <div>
                            <span className="font-semibold">
                              AI Generated Tests:
                            </span>{" "}
                            <span>{nodeInfo.data.aiGeneratedTests}</span>
                          </div>
                          <div>
                            <span className="font-semibold">Tests Passed:</span>{" "}
                            <span>{nodeInfo.data.testsPassed}</span>
                          </div>
                          <div>
                            <span className="font-semibold">Tests Failed:</span>{" "}
                            <span>{nodeInfo.data.testsFailed}</span>
                          </div>
                          <div>
                            <span className="font-semibold">Total Tests:</span>{" "}
                            <span>{nodeInfo.data.totalTests}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <div className="mb-4">
                      <span className="text-sm font-medium text-gray-300">
                        Production Readiness
                      </span>
                      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div
                          className="bg-blue-500 h-4 transition-all duration-700"
                          style={{
                            width: `58%`,
                            minWidth: "8%",
                            borderRadius: "9999px",
                          }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 mt-1 text-right">
                        58% ready for production
                      </div>
                    </div>
                    <div className="mb-4">
                      <span className="text-sm font-medium text-gray-300">
                        Coverage
                      </span>
                      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div
                          className="bg-blue-500 h-4 transition-all duration-700"
                          style={{
                            width: `73%`,
                            minWidth: "8%",
                            borderRadius: "9999px",
                          }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 mt-1 text-right">
                        73% coverage
                      </div>
                    </div>
                    <div className="mb-4">
                      <span className="text-sm font-medium text-gray-300">
                        Repeatability
                      </span>
                      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
                        <div
                          className="bg-blue-500 h-4 transition-all duration-700"
                          style={{
                            width: `94%`,
                            minWidth: "8%",
                            borderRadius: "9999px",
                          }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 mt-1 text-right">
                        94% repeatability
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 text-sm">
                      <div>
                        <span className="font-semibold">
                          AI Generated Tests:
                        </span>{" "}
                        <span>1,028</span>
                      </div>
                      <div>
                        <span className="font-semibold">Tests Passed:</span>{" "}
                        <span>923</span>
                      </div>
                      <div>
                        <span className="font-semibold">Tests Failed:</span>{" "}
                        <span>324</span>
                      </div>
                      <div>
                        <span className="font-semibold">Total Tests:</span>{" "}
                        <span>1,247</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Button at the bottom */}
            {!isAtRoot && nodeInfo && (
              <div className="mt-auto pt-4 border-t border-gray-700">
                {nodeInfo.type === "topic" && (
                  <button
                    onClick={() => {
                      navigate({
                        to: "/ai-agents/$agentId/topics/$topicId",
                        params: { agentId, topicId: selectedNode.id },
                      });
                    }}
                    className="w-full flex items-center justify-center space-x-2 bg-gray-700 hover:bg-gray-600 text-gray-200 px-4 py-2 rounded-md transition-colors border border-gray-600"
                  >
                    <span>Go to topic</span>
                  </button>
                )}
                {nodeInfo.type === "test" && (
                  <button
                    onClick={() => {
                      const testId = selectedNode.testData?.id;
                      if (testId) {
                        navigate({
                          to: "/test-runs/$testRunId/test/$testId",
                          params: { testRunId: "latest", testId },
                        });
                      }
                    }}
                    className="w-full flex items-center justify-center space-x-2 bg-gray-700 hover:bg-gray-600 text-gray-200 px-4 py-2 rounded-md transition-colors border border-gray-600"
                  >
                    <span>Go to test</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Improvement Actions */}
      <ImprovementActions />
    </div>
  );
};
